import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FaBookOpen,
    FaClock,
    FaDownload,
    FaEdit,
    FaFile,
    FaFileAlt,
    FaFileExcel,
    FaFilePdf,
    FaFilePowerpoint,
    FaFileWord,
    FaImage,
    FaLayerGroup,
    FaPaperclip,
    FaSave,
    FaTrash,
    FaUpload,
    FaVideo,
} from "react-icons/fa";

import Swal from "sweetalert2";

import api from "../../../api/api";

const ViewCoursesEdit = ({
    course,
    onClose,
    onUpdated,
    onDeleted,
}) => {
    const [form, setForm] = useState({
        name: "",
        facilitator_name: "",
        description: "",
        category: "",
        duration: "",
        lessons: "",
        level: "",
        price: "",
        old_price: "",
        slots: 0,
    });

    const [image, setImage] = useState(null);
    const [video, setVideo] = useState(null);

    const [removeImage, setRemoveImage] = useState(false);
    const [removeVideo, setRemoveVideo] = useState(false);

    const [resources, setResources] = useState([]);
    const [resourceFile, setResourceFile] = useState(null);
    const [resourceTitle, setResourceTitle] = useState("");
    const [resourceDescription, setResourceDescription] = useState("");

    const [loadingResources, setLoadingResources] =
        useState(false);

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const token = localStorage.getItem("access");

    const headers = {
        Authorization: `Bearer ${token}`,
    };

    const allowedResourceExtensions = useMemo(
        () => [
            ".pdf",
            ".doc",
            ".docx",
            ".xls",
            ".xlsx",
            ".ppt",
            ".pptx",
            ".csv",
            ".txt",
        ],
        []
    );

    const getMediaUrl = (value) => {
        if (!value) {
            return "";
        }

        if (
            value.startsWith("http://") ||
            value.startsWith("https://") ||
            value.startsWith("blob:")
        ) {
            return value;
        }

        if (value.startsWith("/")) {
            return value;
        }

        const baseUrl =
            api.defaults?.baseURL || "";

        if (baseUrl) {
            return `${baseUrl.replace(/\/$/, "")}/${value.replace(
                /^\//,
                ""
            )}`;
        }

        return `/${value}`;
    };

    const getResourceUrl = (resource) => {
        const value =
            resource?.download_url ||
            resource?.file_url ||
            resource?.file;

        if (!value) {
            return "";
        }

        if (
            value.startsWith("http://") ||
            value.startsWith("https://") ||
            value.startsWith("blob:")
        ) {
            return value;
        }

        const baseUrl =
            api.defaults?.baseURL || "";

        if (baseUrl) {
            return `${baseUrl.replace(/\/$/, "")}/${value.replace(
                /^\//,
                ""
            )}`;
        }

        return `/${value.replace(/^\//, "")}`;
    };

    const getResourceExtension = (filename) => {
        if (!filename) {
            return "";
        }

        const cleanName =
            String(filename)
                .split("?")[0]
                .split("#")[0];

        const parts =
            cleanName.split(".");

        if (parts.length < 2) {
            return "";
        }

        return `.${parts.pop().toLowerCase()}`;
    };

    const getResourceIcon = (filename) => {
        const extension =
            getResourceExtension(filename);

        if (extension === ".pdf") {
            return <FaFilePdf />;
        }

        if (
            extension === ".doc" ||
            extension === ".docx"
        ) {
            return <FaFileWord />;
        }

        if (
            extension === ".xls" ||
            extension === ".xlsx" ||
            extension === ".csv"
        ) {
            return <FaFileExcel />;
        }

        if (
            extension === ".ppt" ||
            extension === ".pptx"
        ) {
            return <FaFilePowerpoint />;
        }

        if (extension === ".txt") {
            return <FaFileAlt />;
        }

        return <FaFile />;
    };

    const formatFileSize = (size) => {
        if (!size || size <= 0) {
            return "0 KB";
        }

        if (size < 1024) {
            return `${size} B`;
        }

        if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(1)} KB`;
        }

        return `${(size / 1024 / 1024).toFixed(2)} MB`;
    };

    const getErrorMessage = (
        error,
        fallback
    ) => {
        const errorData =
            error?.response?.data;

        if (
            typeof errorData === "string" &&
            errorData.trim()
        ) {
            return errorData;
        }

        if (errorData?.detail) {
            return String(errorData.detail);
        }

        if (
            errorData &&
            typeof errorData === "object"
        ) {
            const messages =
                Object.entries(errorData)
                    .flatMap(
                        ([field, value]) => {
                            const values =
                                Array.isArray(value)
                                    ? value
                                    : [value];

                            return values.map(
                                (message) =>
                                    `${field}: ${message}`
                            );
                        }
                    )
                    .filter(Boolean);

            if (messages.length) {
                return messages.join("\n");
            }
        }

        if (error?.message) {
            return error.message;
        }

        return fallback;
    };

    const showCayepAlert = ({
        type = "info",
        title = "",
        text = "",
        timer = null,
        showConfirmButton = true,
        showCancelButton = false,
        confirmButtonText = "OK",
        cancelButtonText = "Cancel",
    }) => {
        const colors = {
            success: {
                accent: "#4FAE22",
                background: "#F4FAEF",
            },
            error: {
                accent: "#C62828",
                background: "#FFF5F5",
            },
            warning: {
                accent: "#EFA81C",
                background: "#FFF9E8",
            },
            info: {
                accent: "#0F6DAB",
                background: "#F2F8FC",
            },
        };

        const selected =
            colors[type] || colors.info;

        return Swal.fire({
            icon: type,
            title,
            text,
            background: selected.background,
            color: "#142536",
            showConfirmButton,
            showCancelButton,
            confirmButtonText,
            cancelButtonText,
            confirmButtonColor: "#062A4B",
            cancelButtonColor: "#667085",
            timer: timer || undefined,
            timerProgressBar: Boolean(timer),
            reverseButtons: true,
            buttonsStyling: true,
            customClass: {
                popup: "cayep-edit-swal-popup",
                title: "cayep-edit-swal-title",
                htmlContainer:
                    "cayep-edit-swal-text",
                confirmButton:
                    "cayep-edit-swal-confirm",
                cancelButton:
                    "cayep-edit-swal-cancel",
            },
            didOpen: (popup) => {
                popup.style.borderTop =
                    `5px solid ${selected.accent}`;

                popup.style.boxShadow =
                    "0 22px 60px rgba(6,42,75,0.22)";
            },
        });
    };

    const fetchResources = async (
        courseId
    ) => {
        if (!courseId) {
            setResources([]);
            return;
        }

        try {
            setLoadingResources(true);

            const response =
                await api.get(
                    `api/users/subjects/${courseId}/resources/`,
                    {
                        headers,
                    }
                );

            const data =
                response.data;

            if (Array.isArray(data)) {
                setResources(data);
            } else if (
                Array.isArray(data?.results)
            ) {
                setResources(
                    data.results
                );
            } else {
                setResources([]);
            }
        } catch (error) {
            console.error(
                "Fetch course resources error:",
                error
            );

            setResources([]);
        } finally {
            setLoadingResources(false);
        }
    };

    useEffect(() => {
        if (!course) {
            return;
        }

        setForm({
            name: course.name || "",
            facilitator_name:
                course.facilitator_name ||
                course.facilitatorName ||
                course.instructor_name ||
                "",
            description:
                course.description || "",
            category:
                course.category || "",
            duration:
                course.duration || "",
            lessons:
                course.lessons || "",
            level:
                course.level || "",
            price:
                course.price ?? "",
            old_price:
                course.old_price ??
                course.oldPrice ??
                "",
            slots:
                course.slots ?? 0,
        });

        setImage(null);
        setVideo(null);
        setRemoveImage(false);
        setRemoveVideo(false);

        setResourceFile(null);
        setResourceTitle("");
        setResourceDescription("");

        fetchResources(course.id);
    }, [course]);

    const handleChange = (event) => {
        const {
            name,
            value,
        } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleImageChange = (
        event
    ) => {
        const file =
            event.target.files?.[0] ||
            null;

        setImage(file);

        if (file) {
            setRemoveImage(false);
        }
    };

    const handleVideoChange = (
        event
    ) => {
        const file =
            event.target.files?.[0] ||
            null;

        setVideo(file);

        if (file) {
            setRemoveVideo(false);
        }
    };

    const handleResourceChange = (
        event
    ) => {
        const file =
            event.target.files?.[0] ||
            null;

        if (!file) {
            return;
        }

        const extension =
            getResourceExtension(
                file.name
            );

        if (
            !allowedResourceExtensions.includes(
                extension
            )
        ) {
            showCayepAlert({
                type: "warning",
                title: "Unsupported Resource",
                text:
                    "Allowed files are PDF, Word, Excel, PowerPoint, CSV and TXT.",
            });

            event.target.value = "";
            return;
        }

        const maxSize =
            50 * 1024 * 1024;

        if (file.size > maxSize) {
            showCayepAlert({
                type: "warning",
                title: "File Too Large",
                text:
                    "The learning resource must not be larger than 50 MB.",
            });

            event.target.value = "";
            return;
        }

        setResourceFile(file);

        if (!resourceTitle.trim()) {
            const filename =
                file.name.replace(
                    /\.[^/.]+$/,
                    ""
                );

            setResourceTitle(
                filename
            );
        }
    };

    const removePendingResource = () => {
        setResourceFile(null);
        setResourceTitle("");
        setResourceDescription("");
    };

    const openResource = (
        resource
    ) => {
        const url =
            getResourceUrl(resource);

        if (!url) {
            showCayepAlert({
                type: "error",
                title: "Resource Unavailable",
                text:
                    "The resource file could not be opened.",
            });

            return;
        }

        window.open(
            url,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const deleteResource = async (
        resource
    ) => {
        if (
            !course?.id ||
            !resource?.id
        ) {
            return;
        }

        const result =
            await showCayepAlert({
                type: "warning",
                title: "Delete Resource?",
                text:
                    `This will permanently remove "${resource.title || resource.original_filename || "this resource"}" from the course.`,
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText:
                    "Yes, Delete",
                cancelButtonText:
                    "Cancel",
            });

        if (!result.isConfirmed) {
            return;
        }

        try {
            await api.delete(
                `api/users/subjects/${course.id}/resources/${resource.id}/`,
                {
                    headers,
                }
            );

            setResources(
                (previous) =>
                    previous.filter(
                        (item) =>
                            item.id !==
                            resource.id
                    )
            );

            showCayepAlert({
                type: "success",
                title: "Resource Removed",
                text:
                    "The learning resource has been removed.",
                timer: 1500,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error(
                "Delete resource error:",
                error
            );

            showCayepAlert({
                type: "error",
                title: "Delete Failed",
                text: getErrorMessage(
                    error,
                    "Unable to remove this learning resource."
                ),
            });
        }
    };

    const uploadPendingResource = async (
        courseId
    ) => {
        if (
            !courseId ||
            !resourceFile
        ) {
            return null;
        }

        const formData =
            new FormData();

        formData.append(
            "file",
            resourceFile,
            resourceFile.name
        );

        formData.append(
            "title",
            resourceTitle.trim() ||
                resourceFile.name.replace(
                    /\.[^/.]+$/,
                    ""
                )
        );

        formData.append(
            "description",
            resourceDescription.trim()
        );

        const response =
            await api.post(
                `api/users/subjects/${courseId}/resources/`,
                formData,
                {
                    headers: {
                        ...headers,
                        "Content-Type":
                            "multipart/form-data",
                    },
                }
            );

        return response.data;
    };

    const updateCourse = async (
        event
    ) => {
        event.preventDefault();

        if (!course?.id) {
            return;
        }

        if (!form.name.trim()) {
            showCayepAlert({
                type: "warning",
                title: "Course Name Required",
                text:
                    "Please enter the course name.",
            });

            return;
        }

        if (
            !form.facilitator_name.trim()
        ) {
            showCayepAlert({
                type: "warning",
                title: "Facilitator Required",
                text:
                    "Please enter the facilitator or instructor name.",
            });

            return;
        }

        const parsedSlots = Number(
            form.slots
        );

        if (
            !Number.isInteger(
                parsedSlots
            ) ||
            parsedSlots < 0
        ) {
            showCayepAlert({
                type: "warning",
                title: "Invalid Slots",
                text:
                    "Course slots must be a whole number of 0 or greater. Use 0 for open enrollment.",
            });

            return;
        }

        try {
            setSaving(true);

            const formData =
                new FormData();

            formData.append(
                "name",
                form.name.trim()
            );

            formData.append(
                "facilitator_name",
                form.facilitator_name.trim()
            );

            formData.append(
                "description",
                form.description.trim()
            );

            formData.append(
                "category",
                form.category.trim()
            );

            formData.append(
                "duration",
                form.duration.trim()
            );

            formData.append(
                "lessons",
                form.lessons.trim()
            );

            formData.append(
                "level",
                form.level.trim()
            );

            formData.append(
                "price",
                String(form.price ?? "").trim()
            );

            formData.append(
                "old_price",
                String(
                    form.old_price ?? ""
                ).trim()
            );

            formData.append(
                "slots",
                String(parsedSlots)
            );

            if (image) {
                formData.append(
                    "image",
                    image,
                    image.name
                );
            } else if (
                removeImage
            ) {
                formData.append(
                    "image",
                    ""
                );
            }

            if (video) {
                formData.append(
                    "video",
                    video,
                    video.name
                );
            } else if (
                removeVideo
            ) {
                formData.append(
                    "video",
                    ""
                );
            }

            const response =
                await api.patch(
                    `api/users/subjects/${course.id}/`,
                    formData,
                    {
                        headers: {
                            ...headers,
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

            const updatedCourse =
                response.data;

            let resourceUploadFailed =
                false;

            if (resourceFile) {
                try {
                    await uploadPendingResource(
                        course.id
                    );

                    await fetchResources(
                        course.id
                    );
                } catch (
                    resourceError
                ) {
                    resourceUploadFailed =
                        true;

                    console.error(
                        "Course updated but resource upload failed:",
                        resourceError
                    );
                }
            }

            if (onUpdated) {
                onUpdated(
                    updatedCourse
                );
            }

            if (
                resourceUploadFailed
            ) {
                showCayepAlert({
                    type: "warning",
                    title: "Course Updated",
                    text:
                        "The course was updated successfully, but the selected learning resource could not be uploaded. You can try adding the resource again.",
                });

                setResourceFile(null);
                setResourceTitle("");
                setResourceDescription("");

                return;
            }

            showCayepAlert({
                type: "success",
                title: "Course Updated",
                text: resourceFile
                    ? "The course and learning resource have been updated successfully."
                    : "The course has been updated successfully.",
                timer: 1800,
                showConfirmButton: false,
            });

            setResourceFile(null);
            setResourceTitle("");
            setResourceDescription("");
        } catch (error) {
            console.error(
                "Update course error:",
                error
            );

            const errorMessage =
                getErrorMessage(
                    error,
                    "Unable to update the course. Please try again."
                );

            showCayepAlert({
                type: "error",
                title: "Update Failed",
                text: errorMessage,
            });
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = async () => {
        if (!course?.id) {
            return;
        }

        const result =
            await showCayepAlert({
                type: "warning",
                title: "Delete Course?",
                text:
                    `This will permanently delete "${course.name || "this course"}" and its associated course record. This action cannot be undone.`,
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText:
                    "Yes, Delete Course",
                cancelButtonText:
                    "Cancel",
            });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setDeleting(true);

            await api.delete(
                `api/users/subjects/${course.id}/`,
                {
                    headers,
                }
            );

            await showCayepAlert({
                type: "success",
                title: "Course Deleted",
                text:
                    "The course has been permanently deleted.",
                timer: 1700,
                showConfirmButton: false,
            });

            if (onDeleted) {
                await onDeleted(
                    course.id
                );
            }
        } catch (error) {
            console.error(
                "Delete course error:",
                error
            );

            showCayepAlert({
                type: "error",
                title: "Delete Failed",
                text: getErrorMessage(
                    error,
                    "Unable to delete this course. Please try again."
                ),
            });
        } finally {
            setDeleting(false);
        }
    };

    if (!course) {
        return null;
    }

    return (
        <div
            className="cayep-course-edit-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cayep-edit-course-title"
        >
            <div
                className="cayep-course-edit-modal"
                onClick={(event) =>
                    event.stopPropagation()
                }
            >
                <div className="cayep-course-edit-header">
                    <div>
                        <span>
                            COURSE MANAGEMENT
                        </span>

                        <h2 id="cayep-edit-course-title">
                            Edit Course
                        </h2>

                        <p>
                            Update course information,
                            media and learning resources.
                        </p>
                    </div>

                    <button
                        type="button"
                        className="cayep-course-edit-close"
                        onClick={onClose}
                        disabled={
                            saving ||
                            deleting
                        }
                        aria-label="Close edit course"
                    >
                        ×
                    </button>
                </div>

                <form
                    onSubmit={
                        updateCourse
                    }
                >
                    <div className="cayep-course-edit-body">
                        <div className="cayep-course-edit-section">
                            <div className="cayep-course-edit-section-title">
                                <FaBookOpen />

                                <div>
                                    <h3>
                                        Course Information
                                    </h3>

                                    <p>
                                        Update the information
                                        displayed to learners.
                                    </p>
                                </div>
                            </div>

                            <div className="cayep-course-edit-grid">
                                <label className="cayep-course-edit-field full">
                                    <span>
                                        Course Name
                                    </span>

                                    <input
                                        type="text"
                                        name="name"
                                        value={
                                            form.name
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={
                                            100
                                        }
                                        required
                                        placeholder="Enter course name"
                                    />
                                </label>

                                <label className="cayep-course-edit-field full">
                                    <span>
                                        Facilitator / Instructor
                                    </span>

                                    <div className="cayep-facilitator-field">
                                        <FaEdit />

                                        <input
                                            type="text"
                                            name="facilitator_name"
                                            value={
                                                form.facilitator_name
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            maxLength={
                                                150
                                            }
                                            required
                                            placeholder="Enter facilitator or instructor name"
                                        />
                                    </div>
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Category
                                    </span>

                                    <input
                                        type="text"
                                        name="category"
                                        value={
                                            form.category
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={
                                            100
                                        }
                                        placeholder="e.g. Leadership"
                                    />
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Level
                                    </span>

                                    <input
                                        type="text"
                                        name="level"
                                        value={
                                            form.level
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={
                                            50
                                        }
                                        placeholder="e.g. Beginner"
                                    />
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Duration
                                    </span>

                                    <div className="cayep-course-input-icon">
                                        <FaClock />

                                        <input
                                            type="text"
                                            name="duration"
                                            value={
                                                form.duration
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            maxLength={
                                                100
                                            }
                                            placeholder="e.g. 6 weeks"
                                        />
                                    </div>
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Lessons
                                    </span>

                                    <div className="cayep-course-input-icon">
                                        <FaLayerGroup />

                                        <input
                                            type="text"
                                            name="lessons"
                                            value={
                                                form.lessons
                                            }
                                            onChange={
                                                handleChange
                                            }
                                            maxLength={
                                                100
                                            }
                                            placeholder="e.g. 12 lessons"
                                        />
                                    </div>
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Course Slots
                                    </span>

                                    <input
                                        type="number"
                                        name="slots"
                                        value={
                                            form.slots
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        min="0"
                                        step="1"
                                        placeholder="e.g. 30"
                                    />
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Current Price
                                    </span>

                                    <input
                                        type="text"
                                        name="price"
                                        value={
                                            form.price
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={
                                            50
                                        }
                                        placeholder="e.g. $150"
                                    />
                                </label>

                                <label className="cayep-course-edit-field">
                                    <span>
                                        Old Price
                                    </span>

                                    <input
                                        type="text"
                                        name="old_price"
                                        value={
                                            form.old_price
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength={
                                            50
                                        }
                                        placeholder="e.g. $200"
                                    />
                                </label>

                                <label className="cayep-course-edit-field full">
                                    <span>
                                        Description
                                    </span>

                                    <textarea
                                        name="description"
                                        value={
                                            form.description
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        rows={
                                            6
                                        }
                                        placeholder="Describe this course..."
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="cayep-course-edit-section">
                            <div className="cayep-course-edit-section-title">
                                <FaImage />

                                <div>
                                    <h3>
                                        Course Thumbnail
                                    </h3>

                                    <p>
                                        Replace or remove the
                                        existing course image.
                                    </p>
                                </div>
                            </div>

                            <div className="cayep-course-media-box">
                                <div className="cayep-course-current-media">
                                    {course.image &&
                                    !removeImage &&
                                    !image ? (
                                        <img
                                            src={getMediaUrl(
                                                course.image
                                            )}
                                            alt={
                                                course.name
                                            }
                                        />
                                    ) : image ? (
                                        <img
                                            src={URL.createObjectURL(
                                                image
                                            )}
                                            alt="New course preview"
                                        />
                                    ) : (
                                        <FaImage />
                                    )}
                                </div>

                                <div className="cayep-course-media-actions">
                                    <label className="cayep-upload-button">
                                        <FaUpload />

                                        {image
                                            ? "Change Image"
                                            : "Upload New Image"}

                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={
                                                handleImageChange
                                            }
                                        />
                                    </label>

                                    {course.image &&
                                        !removeImage && (
                                            <button
                                                type="button"
                                                className="cayep-remove-media"
                                                onClick={() =>
                                                    setRemoveImage(
                                                        true
                                                    )
                                                }
                                            >
                                                <FaTrash />
                                                Remove Image
                                            </button>
                                        )}

                                    {removeImage && (
                                        <button
                                            type="button"
                                            className="cayep-restore-media"
                                            onClick={() =>
                                                setRemoveImage(
                                                    false
                                                )
                                            }
                                        >
                                            Restore Existing
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="cayep-course-edit-section">
                            <div className="cayep-course-edit-section-title">
                                <FaVideo />

                                <div>
                                    <h3>
                                        Course Video
                                    </h3>

                                    <p>
                                        Replace or remove the
                                        published course video.
                                    </p>
                                </div>
                            </div>

                            <div className="cayep-course-video-box">
                                <div className="cayep-course-video-status">
                                    <FaVideo />

                                    <div>
                                        <strong>
                                            {course.video &&
                                            !removeVideo
                                                ? "Video Published"
                                                : "No Published Video"}
                                        </strong>

                                        <small>
                                            {video
                                                ? video.name
                                                : course.video &&
                                                  !removeVideo
                                                ? "An existing course video is attached."
                                                : "Upload a new video when needed."}
                                        </small>
                                    </div>
                                </div>

                                <div className="cayep-course-media-actions">
                                    <label className="cayep-upload-button">
                                        <FaUpload />

                                        {video
                                            ? "Change Video"
                                            : "Upload New Video"}

                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={
                                                handleVideoChange
                                            }
                                        />
                                    </label>

                                    {course.video &&
                                        !removeVideo && (
                                            <button
                                                type="button"
                                                className="cayep-remove-media"
                                                onClick={() =>
                                                    setRemoveVideo(
                                                        true
                                                    )
                                                }
                                            >
                                                <FaTrash />
                                                Remove Video
                                            </button>
                                        )}

                                    {removeVideo && (
                                        <button
                                            type="button"
                                            className="cayep-restore-media"
                                            onClick={() =>
                                                setRemoveVideo(
                                                    false
                                                )
                                            }
                                        >
                                            Keep Existing Video
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="cayep-course-edit-section">
                            <div className="cayep-course-edit-section-title">
                                <FaPaperclip />

                                <div>
                                    <h3>
                                        Learning Resources
                                    </h3>

                                    <p>
                                        Add PDF, Word, Excel,
                                        PowerPoint, CSV or TXT
                                        learning materials.
                                    </p>
                                </div>
                            </div>

                            <div className="cayep-resource-upload-panel">
                                <div className="cayep-resource-upload-top">
                                    <label className="cayep-resource-upload-button">
                                        <FaUpload />

                                        Select Resource

                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
                                            onChange={
                                                handleResourceChange
                                            }
                                        />
                                    </label>

                                    <span>
                                        Maximum 50 MB
                                    </span>
                                </div>

                                {resourceFile && (
                                    <div className="cayep-pending-resource">
                                        <div className="cayep-pending-resource-icon">
                                            {getResourceIcon(
                                                resourceFile.name
                                            )}
                                        </div>

                                        <div className="cayep-pending-resource-info">
                                            <strong>
                                                {resourceFile.name}
                                            </strong>

                                            <span>
                                                {formatFileSize(
                                                    resourceFile.size
                                                )}
                                            </span>
                                        </div>

                                        <button
                                            type="button"
                                            className="cayep-resource-remove-pending"
                                            onClick={
                                                removePendingResource
                                            }
                                            disabled={
                                                saving
                                            }
                                        >
                                            <FaTrash />
                                            Remove
                                        </button>
                                    </div>
                                )}

                                {resourceFile && (
                                    <div className="cayep-resource-details-grid">
                                        <label>
                                            <span>
                                                Resource Title
                                            </span>

                                            <input
                                                type="text"
                                                value={
                                                    resourceTitle
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setResourceTitle(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                maxLength={
                                                    200
                                                }
                                                placeholder="Enter resource title"
                                            />
                                        </label>

                                        <label>
                                            <span>
                                                Description
                                            </span>

                                            <textarea
                                                value={
                                                    resourceDescription
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setResourceDescription(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                rows={
                                                    3
                                                }
                                                placeholder="Optional description for this resource"
                                            />
                                        </label>
                                    </div>
                                )}

                                <div className="cayep-resource-save-notice">
                                    <FaSave />

                                    <span>
                                        {resourceFile
                                            ? "This resource will be uploaded when you click Save Changes."
                                            : "Select a resource above. It will be uploaded together with the course update."}
                                    </span>
                                </div>
                            </div>

                            <div className="cayep-existing-resources">
                                <div className="cayep-existing-resources-header">
                                    <div>
                                        <strong>
                                            Existing Learning Resources
                                        </strong>

                                        <small>
                                            Resources already
                                            attached to this course
                                        </small>
                                    </div>

                                    <span>
                                        {resources.length}
                                    </span>
                                </div>

                                {loadingResources ? (
                                    <div className="cayep-resource-empty">
                                        Loading resources...
                                    </div>
                                ) : resources.length ===
                                  0 ? (
                                    <div className="cayep-resource-empty">
                                        <FaPaperclip />

                                        <span>
                                            No learning resources
                                            have been added to
                                            this course yet.
                                        </span>
                                    </div>
                                ) : (
                                    <div className="cayep-resource-list">
                                        {resources.map(
                                            (
                                                resource
                                            ) => (
                                                <div
                                                    className="cayep-existing-resource"
                                                    key={
                                                        resource.id
                                                    }
                                                >
                                                    <div className="cayep-existing-resource-icon">
                                                        {getResourceIcon(
                                                            resource.original_filename ||
                                                                resource.file_name ||
                                                                resource.name ||
                                                                resource.file
                                                        )}
                                                    </div>

                                                    <div className="cayep-existing-resource-info">
                                                        <strong>
                                                            {resource.title ||
                                                                resource.original_filename ||
                                                                resource.file_name ||
                                                                "Learning Resource"}
                                                        </strong>

                                                        <span>
                                                            {resource.description ||
                                                                "Course learning material"}
                                                        </span>
                                                    </div>

                                                    <div className="cayep-existing-resource-actions">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                openResource(
                                                                    resource
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                            title="Open resource"
                                                        >
                                                            <FaDownload />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="danger"
                                                            onClick={() =>
                                                                deleteResource(
                                                                    resource
                                                                )
                                                            }
                                                            disabled={
                                                                saving
                                                            }
                                                            title="Delete resource"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="cayep-course-edit-footer">
                        <button
                            type="button"
                            className="cayep-course-delete"
                            onClick={
                                confirmDelete
                            }
                            disabled={
                                saving ||
                                deleting
                            }
                        >
                            <FaTrash />

                            {deleting
                                ? "Deleting..."
                                : "Delete Course"}
                        </button>

                        <div className="cayep-course-edit-footer-right">
                            <button
                                type="button"
                                className="cayep-course-cancel"
                                onClick={
                                    onClose
                                }
                                disabled={
                                    saving ||
                                    deleting
                                }
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="cayep-course-save"
                                disabled={
                                    saving ||
                                    deleting
                                }
                            >
                                <FaSave />

                                {saving
                                    ? "Saving Course..."
                                    : "Save Changes"}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            <style>{`
                .cayep-course-edit-overlay {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 25px;
                    box-sizing: border-box;
                    background: rgba(2, 8, 13, 0.82);
                    backdrop-filter: blur(7px);
                    overflow-y: auto;
                }

                .cayep-course-edit-modal {
                    width: min(100%, 980px);
                    max-height: calc(100vh - 50px);
                    overflow: hidden;
                    border: 1px solid rgba(246, 201, 68, 0.18);
                    background: #081722;
                    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
                    color: #F0EDEA;
                }

                .cayep-course-edit-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: 20px;
                    padding: 25px 28px;
                    border-bottom: 1px solid rgba(240, 237, 234, 0.08);
                    background: linear-gradient(
                        135deg,
                        rgba(6, 42, 75, 0.95),
                        rgba(7, 19, 29, 0.98)
                    );
                }

                .cayep-course-edit-header span {
                    color: #F6C944;
                    font-size: 8px;
                    font-weight: 800;
                    letter-spacing: 2.5px;
                }

                .cayep-course-edit-header h2 {
                    margin: 6px 0;
                    font-size: 24px;
                }

                .cayep-course-edit-header p {
                    margin: 0;
                    color: #87939A;
                    font-size: 11px;
                }

                .cayep-course-edit-close {
                    width: 38px;
                    height: 38px;
                    flex-shrink: 0;
                    border: 1px solid rgba(240, 237, 234, 0.1);
                    background: rgba(255, 255, 255, 0.04);
                    color: #A9B1B5;
                    cursor: pointer;
                    font-size: 24px;
                    line-height: 1;
                }

                .cayep-course-edit-close:hover {
                    color: #F6C944;
                    border-color: rgba(246, 201, 68, 0.4);
                }

                .cayep-course-edit-body {
                    max-height: calc(100vh - 220px);
                    overflow-y: auto;
                    padding: 25px 28px;
                }

                .cayep-course-edit-body::-webkit-scrollbar {
                    width: 7px;
                }

                .cayep-course-edit-body::-webkit-scrollbar-track {
                    background: rgba(255,255,255,0.025);
                }

                .cayep-course-edit-body::-webkit-scrollbar-thumb {
                    background: rgba(246,201,68,0.35);
                    border-radius: 20px;
                }

                .cayep-course-edit-section {
                    margin-bottom: 25px;
                    padding: 21px;
                    border: 1px solid rgba(240, 237, 234, 0.08);
                    background: rgba(255, 255, 255, 0.018);
                }

                .cayep-course-edit-section:last-child {
                    margin-bottom: 0;
                }

                .cayep-course-edit-section-title {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 20px;
                }

                .cayep-course-edit-section-title > svg {
                    flex-shrink: 0;
                    margin-top: 2px;
                    color: #F6C944;
                    font-size: 19px;
                }

                .cayep-course-edit-section-title h3 {
                    margin: 0;
                    font-size: 15px;
                }

                .cayep-course-edit-section-title p {
                    margin: 5px 0 0;
                    color: #758188;
                    font-size: 10px;
                }

                .cayep-course-edit-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: 17px;
                }

                .cayep-course-edit-field {
                    display: flex;
                    flex-direction: column;
                    gap: 7px;
                    min-width: 0;
                }

                .cayep-course-edit-field.full {
                    grid-column: 1 / -1;
                }

                .cayep-course-edit-field > span,
                .cayep-resource-details-grid label > span {
                    color: #A8B0B5;
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 0.4px;
                }

                .cayep-course-edit-field input,
                .cayep-course-edit-field textarea,
                .cayep-facilitator-field,
                .cayep-resource-details-grid input,
                .cayep-resource-details-grid textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid rgba(240, 237, 234, 0.11);
                    outline: none;
                    background: rgba(0, 0, 0, 0.18);
                    color: #F0EDEA;
                    font-family: inherit;
                    font-size: 11px;
                    transition: border-color 0.2s ease, background 0.2s ease;
                }

                .cayep-course-edit-field input,
                .cayep-course-edit-field textarea {
                    padding: 12px 13px;
                }

                .cayep-course-edit-field textarea {
                    resize: vertical;
                    min-height: 110px;
                    line-height: 1.7;
                }

                .cayep-course-edit-field input:focus,
                .cayep-course-edit-field textarea:focus,
                .cayep-facilitator-field:focus-within,
                .cayep-resource-details-grid input:focus,
                .cayep-resource-details-grid textarea:focus {
                    border-color: rgba(246, 201, 68, 0.7);
                    background: rgba(246, 201, 68, 0.025);
                }

                .cayep-facilitator-field {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding-left: 12px;
                }

                .cayep-facilitator-field svg {
                    flex-shrink: 0;
                    color: #F6C944;
                }

                .cayep-facilitator-field input {
                    border: 0;
                    padding: 12px 13px 12px 0;
                    background: transparent;
                }

                .cayep-facilitator-field input:focus {
                    background: transparent;
                }

                .cayep-course-input-icon {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .cayep-course-input-icon > svg {
                    position: absolute;
                    left: 13px;
                    color: #F6C944;
                    font-size: 11px;
                    pointer-events: none;
                }

                .cayep-course-input-icon input {
                    padding-left: 34px;
                }

                .cayep-course-media-box,
                .cayep-course-video-box {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    padding: 15px;
                    border: 1px solid rgba(240, 237, 234, 0.07);
                    background: rgba(0, 0, 0, 0.13);
                }

                .cayep-course-current-media {
                    width: 180px;
                    height: 105px;
                    flex-shrink: 0;
                    display: grid;
                    place-items: center;
                    overflow: hidden;
                    background: #063A6B;
                    color: #F6C944;
                }

                .cayep-course-current-media img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .cayep-course-current-media svg {
                    font-size: 30px;
                }

                .cayep-course-media-actions {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 9px;
                    flex-wrap: wrap;
                }

                .cayep-upload-button,
                .cayep-remove-media,
                .cayep-restore-media {
                    display: inline-flex;
                    align-items: center;
                    gap: 7px;
                    min-height: 36px;
                    box-sizing: border-box;
                    padding: 0 12px;
                    border: 1px solid rgba(240, 237, 234, 0.12);
                    cursor: pointer;
                    font-family: inherit;
                    font-size: 9px;
                    font-weight: 700;
                }

                .cayep-upload-button {
                    position: relative;
                    overflow: hidden;
                    border-color: rgba(246, 201, 68, 0.45);
                    background: rgba(246, 201, 68, 0.08);
                    color: #F6C944;
                }

                .cayep-upload-button input,
                .cayep-resource-upload-button input {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                    cursor: pointer;
                }

                .cayep-remove-media {
                    border-color: rgba(180, 35, 24, 0.3);
                    background: rgba(180, 35, 24, 0.08);
                    color: #F28B82;
                }

                .cayep-restore-media {
                    border-color: rgba(79, 174, 34, 0.3);
                    background: rgba(79, 174, 34, 0.06);
                    color: #8FD06E;
                }

                .cayep-upload-button:hover,
                .cayep-remove-media:hover,
                .cayep-restore-media:hover {
                    filter: brightness(1.15);
                }

                .cayep-course-video-status {
                    display: flex;
                    align-items: center;
                    gap: 13px;
                    min-width: 0;
                }

                .cayep-course-video-status > svg {
                    flex-shrink: 0;
                    color: #F6C944;
                    font-size: 22px;
                }

                .cayep-course-video-status strong {
                    display: block;
                    color: #F0EDEA;
                    font-size: 11px;
                }

                .cayep-course-video-status small {
                    display: block;
                    max-width: 400px;
                    margin-top: 4px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #707C82;
                    font-size: 9px;
                }

                .cayep-resource-upload-panel {
                    padding: 16px;
                    border: 1px solid rgba(246, 201, 68, 0.13);
                    background: rgba(6, 42, 75, 0.12);
                }

                .cayep-resource-upload-top {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 12px;
                }

                .cayep-resource-upload-top > span {
                    color: #707C82;
                    font-size: 9px;
                }

                .cayep-resource-upload-button {
                    position: relative;
                    overflow: hidden;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    min-height: 38px;
                    padding: 0 14px;
                    border: 1px solid rgba(246, 201, 68, 0.45);
                    background: rgba(246, 201, 68, 0.08);
                    color: #F6C944;
                    cursor: pointer;
                    font-size: 9px;
                    font-weight: 800;
                }

                .cayep-pending-resource {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 11px;
                    border: 1px solid rgba(79, 174, 34, 0.25);
                    background: rgba(79, 174, 34, 0.055);
                }

                .cayep-pending-resource-icon {
                    width: 40px;
                    height: 40px;
                    flex-shrink: 0;
                    display: grid;
                    place-items: center;
                    background: rgba(246, 201, 68, 0.1);
                    color: #F6C944;
                    font-size: 19px;
                }

                .cayep-pending-resource-info {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }

                .cayep-pending-resource-info strong {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #F0EDEA;
                    font-size: 10px;
                }

                .cayep-pending-resource-info span {
                    color: #8C989E;
                    font-size: 9px;
                }

                .cayep-resource-remove-pending {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    border: 0;
                    background: transparent;
                    color: #F28B82;
                    cursor: pointer;
                    font-family: inherit;
                    font-size: 9px;
                    font-weight: 700;
                }

                .cayep-resource-details-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                    margin-top: 12px;
                }

                .cayep-resource-details-grid label {
                    display: flex;
                    flex-direction: column;
                    gap: 7px;
                }

                .cayep-resource-details-grid input,
                .cayep-resource-details-grid textarea {
                    padding: 10px 11px;
                }

                .cayep-resource-details-grid textarea {
                    resize: vertical;
                    min-height: 74px;
                }

                .cayep-resource-save-notice {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 12px;
                    color: #849197;
                    font-size: 9px;
                    line-height: 1.5;
                }

                .cayep-resource-save-notice svg {
                    flex-shrink: 0;
                    color: #F6C944;
                }

                .cayep-existing-resources {
                    margin-top: 15px;
                    border: 1px solid rgba(240, 237, 234, 0.07);
                    background: rgba(0, 0, 0, 0.13);
                }

                .cayep-existing-resources-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    padding: 13px 14px;
                    border-bottom: 1px solid rgba(240, 237, 234, 0.07);
                }

                .cayep-existing-resources-header strong {
                    display: block;
                    color: #F0EDEA;
                    font-size: 10px;
                }

                .cayep-existing-resources-header small {
                    display: block;
                    margin-top: 4px;
                    color: #6F7B81;
                    font-size: 8px;
                }

                .cayep-existing-resources-header > span {
                    min-width: 25px;
                    height: 25px;
                    display: grid;
                    place-items: center;
                    border-radius: 50%;
                    background: rgba(246, 201, 68, 0.1);
                    color: #F6C944;
                    font-size: 9px;
                    font-weight: 800;
                }

                .cayep-resource-list {
                    display: flex;
                    flex-direction: column;
                }

                .cayep-existing-resource {
                    display: flex;
                    align-items: center;
                    gap: 11px;
                    padding: 11px 14px;
                    border-bottom: 1px solid rgba(240, 237, 234, 0.05);
                }

                .cayep-existing-resource:last-child {
                    border-bottom: 0;
                }

                .cayep-existing-resource-icon {
                    width: 35px;
                    height: 35px;
                    flex-shrink: 0;
                    display: grid;
                    place-items: center;
                    background: rgba(246, 201, 68, 0.08);
                    color: #F6C944;
                    font-size: 16px;
                }

                .cayep-existing-resource-info {
                    flex: 1;
                    min-width: 0;
                }

                .cayep-existing-resource-info strong {
                    display: block;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #E6EAEC;
                    font-size: 10px;
                }

                .cayep-existing-resource-info span {
                    display: block;
                    margin-top: 4px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    color: #69767D;
                    font-size: 8px;
                }

                .cayep-existing-resource-actions {
                    display: flex;
                    gap: 6px;
                }

                .cayep-existing-resource-actions button {
                    width: 30px;
                    height: 30px;
                    display: grid;
                    place-items: center;
                    border: 1px solid rgba(240, 237, 234, 0.1);
                    background: rgba(255,255,255,0.035);
                    color: #F6C944;
                    cursor: pointer;
                }

                .cayep-existing-resource-actions button.danger {
                    color: #F28B82;
                }

                .cayep-existing-resource-actions button:hover {
                    background: rgba(246, 201, 68, 0.08);
                }

                .cayep-existing-resource-actions button.danger:hover {
                    background: rgba(180, 35, 24, 0.1);
                }

                .cayep-existing-resource-actions button:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .cayep-resource-empty {
                    min-height: 75px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 15px;
                    color: #69767D;
                    font-size: 9px;
                    text-align: center;
                }

                .cayep-resource-empty svg {
                    color: #F6C944;
                }

                .cayep-course-edit-footer {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 15px;
                    padding: 17px 28px;
                    border-top: 1px solid rgba(240, 237, 234, 0.08);
                    background: rgba(0, 0, 0, 0.2);
                }

                .cayep-course-edit-footer-right {
                    display: flex;
                    align-items: center;
                    gap: 9px;
                }

                .cayep-course-delete,
                .cayep-course-cancel,
                .cayep-course-save {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    min-height: 40px;
                    padding: 0 16px;
                    cursor: pointer;
                    font-family: inherit;
                    font-size: 10px;
                    font-weight: 800;
                }

                .cayep-course-delete {
                    border: 1px solid rgba(180, 35, 24, 0.35);
                    background: rgba(180, 35, 24, 0.08);
                    color: #F28B82;
                }

                .cayep-course-cancel {
                    border: 1px solid rgba(240, 237, 234, 0.12);
                    background: rgba(255, 255, 255, 0.035);
                    color: #AAB2B6;
                }

                .cayep-course-save {
                    border: 1px solid #F6C944;
                    background: #F6C944;
                    color: #0A0F12;
                }

                .cayep-course-delete:hover {
                    background: rgba(180, 35, 24, 0.15);
                }

                .cayep-course-cancel:hover {
                    color: #F0EDEA;
                }

                .cayep-course-save:hover {
                    filter: brightness(1.06);
                }

                .cayep-course-delete:disabled,
                .cayep-course-cancel:disabled,
                .cayep-course-save:disabled,
                .cayep-course-edit-close:disabled {
                    opacity: 0.45;
                    cursor: not-allowed;
                }

                .cayep-edit-swal-popup {
                    width: min(430px, calc(100vw - 28px)) !important;
                    border-radius: 17px !important;
                    padding: 24px !important;
                    font-family: inherit !important;
                }

                .cayep-edit-swal-title {
                    color: #062A4B !important;
                    font-size: 20px !important;
                    font-weight: 800 !important;
                }

                .cayep-edit-swal-text {
                    color: #526273 !important;
                    font-size: 13px !important;
                    line-height: 1.65 !important;
                }

                .cayep-edit-swal-confirm,
                .cayep-edit-swal-cancel {
                    min-height: 39px !important;
                    border-radius: 8px !important;
                    font-size: 12px !important;
                    font-weight: 700 !important;
                }

                .swal2-timer-progress-bar {
                    background: #F6C944 !important;
                }

                @media (max-width: 720px) {
                    .cayep-course-edit-overlay {
                        align-items: flex-start;
                        padding: 10px;
                    }

                    .cayep-course-edit-modal {
                        max-height: calc(100vh - 20px);
                    }

                    .cayep-course-edit-header {
                        padding: 20px;
                    }

                    .cayep-course-edit-body {
                        padding: 15px;
                        max-height: calc(100vh - 190px);
                    }

                    .cayep-course-edit-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-course-edit-field.full {
                        grid-column: auto;
                    }

                    .cayep-course-media-box,
                    .cayep-course-video-box {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .cayep-course-current-media {
                        width: 100%;
                        height: 180px;
                    }

                    .cayep-course-media-actions {
                        justify-content: flex-start;
                    }

                    .cayep-resource-details-grid {
                        grid-template-columns: 1fr;
                    }

                    .cayep-resource-upload-top {
                        align-items: flex-start;
                        flex-direction: column;
                    }

                    .cayep-pending-resource {
                        align-items: flex-start;
                    }

                    .cayep-resource-remove-pending {
                        flex-shrink: 0;
                    }

                    .cayep-course-edit-footer {
                        align-items: stretch;
                        flex-direction: column;
                        padding: 15px;
                    }

                    .cayep-course-edit-footer-right {
                        width: 100%;
                    }

                    .cayep-course-delete,
                    .cayep-course-cancel,
                    .cayep-course-save {
                        flex: 1;
                    }
                }

                @media (max-width: 480px) {
                    .cayep-course-edit-header h2 {
                        font-size: 20px;
                    }

                    .cayep-course-edit-header p {
                        max-width: 240px;
                        line-height: 1.5;
                    }

                    .cayep-course-edit-section {
                        padding: 15px;
                    }

                    .cayep-existing-resource {
                        align-items: flex-start;
                    }

                    .cayep-existing-resource-actions {
                        flex-direction: column;
                    }

                    .cayep-course-edit-footer-right {
                        flex-direction: column;
                    }

                    .cayep-course-cancel,
                    .cayep-course-save {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default ViewCoursesEdit;




// import React, {
//     useEffect,
//     useMemo,
//     useState,
// } from "react";

// import {
//     FaBookOpen,
//     FaClock,
//     FaDownload,
//     FaEdit,
//     FaFile,
//     FaFileAlt,
//     FaFileExcel,
//     FaFilePdf,
//     FaFilePowerpoint,
//     FaFileWord,
//     FaImage,
//     FaLayerGroup,
//     FaPaperclip,
//     FaSave,
//     FaTrash,
//     FaUpload,
//     FaVideo,
// } from "react-icons/fa";

// import Swal from "sweetalert2";

// import api from "../../../api/api";

// const ViewCoursesEdit = ({
//     course,
//     onClose,
//     onUpdated,
//     onDeleted,
// }) => {
//     const [form, setForm] = useState({
//         name: "",
//         facilitator_name: "",
//         description: "",
//         category: "",
//         duration: "",
//         lessons: "",
//         level: "",
//         price: "",
//         old_price: "",
//     });

//     const [image, setImage] = useState(null);
//     const [video, setVideo] = useState(null);

//     const [removeImage, setRemoveImage] = useState(false);
//     const [removeVideo, setRemoveVideo] = useState(false);

//     const [resources, setResources] = useState([]);
//     const [resourceFile, setResourceFile] = useState(null);
//     const [resourceTitle, setResourceTitle] = useState("");
//     const [resourceDescription, setResourceDescription] = useState("");

//     const [loadingResources, setLoadingResources] =
//         useState(false);

//     const [saving, setSaving] = useState(false);
//     const [deleting, setDeleting] = useState(false);

//     const token = localStorage.getItem("access");

//     const headers = {
//         Authorization: `Bearer ${token}`,
//     };

//     const allowedResourceExtensions = useMemo(
//         () => [
//             ".pdf",
//             ".doc",
//             ".docx",
//             ".xls",
//             ".xlsx",
//             ".ppt",
//             ".pptx",
//             ".csv",
//             ".txt",
//         ],
//         []
//     );

//     const getMediaUrl = (value) => {
//         if (!value) {
//             return "";
//         }

//         if (
//             value.startsWith("http://") ||
//             value.startsWith("https://") ||
//             value.startsWith("blob:")
//         ) {
//             return value;
//         }

//         if (value.startsWith("/")) {
//             return value;
//         }

//         const baseUrl =
//             api.defaults?.baseURL || "";

//         if (baseUrl) {
//             return `${baseUrl.replace(/\/$/, "")}/${value.replace(
//                 /^\//,
//                 ""
//             )}`;
//         }

//         return `/${value}`;
//     };

//     const getResourceUrl = (resource) => {
//         const value =
//             resource?.download_url ||
//             resource?.file_url ||
//             resource?.file;

//         if (!value) {
//             return "";
//         }

//         if (
//             value.startsWith("http://") ||
//             value.startsWith("https://") ||
//             value.startsWith("blob:")
//         ) {
//             return value;
//         }

//         const baseUrl =
//             api.defaults?.baseURL || "";

//         if (baseUrl) {
//             return `${baseUrl.replace(/\/$/, "")}/${value.replace(
//                 /^\//,
//                 ""
//             )}`;
//         }

//         return `/${value.replace(/^\//, "")}`;
//     };

//     const getResourceExtension = (filename) => {
//         if (!filename) {
//             return "";
//         }

//         const cleanName =
//             String(filename)
//                 .split("?")[0]
//                 .split("#")[0];

//         const parts =
//             cleanName.split(".");

//         if (parts.length < 2) {
//             return "";
//         }

//         return `.${parts.pop().toLowerCase()}`;
//     };

//     const getResourceIcon = (filename) => {
//         const extension =
//             getResourceExtension(filename);

//         if (extension === ".pdf") {
//             return <FaFilePdf />;
//         }

//         if (
//             extension === ".doc" ||
//             extension === ".docx"
//         ) {
//             return <FaFileWord />;
//         }

//         if (
//             extension === ".xls" ||
//             extension === ".xlsx" ||
//             extension === ".csv"
//         ) {
//             return <FaFileExcel />;
//         }

//         if (
//             extension === ".ppt" ||
//             extension === ".pptx"
//         ) {
//             return <FaFilePowerpoint />;
//         }

//         if (extension === ".txt") {
//             return <FaFileAlt />;
//         }

//         return <FaFile />;
//     };

//     const formatFileSize = (size) => {
//         if (!size || size <= 0) {
//             return "0 KB";
//         }

//         if (size < 1024) {
//             return `${size} B`;
//         }

//         if (size < 1024 * 1024) {
//             return `${(size / 1024).toFixed(1)} KB`;
//         }

//         return `${(size / 1024 / 1024).toFixed(2)} MB`;
//     };

//     const getErrorMessage = (
//         error,
//         fallback
//     ) => {
//         const errorData =
//             error?.response?.data;

//         if (
//             typeof errorData === "string" &&
//             errorData.trim()
//         ) {
//             return errorData;
//         }

//         if (errorData?.detail) {
//             return String(errorData.detail);
//         }

//         if (
//             errorData &&
//             typeof errorData === "object"
//         ) {
//             const messages =
//                 Object.entries(errorData)
//                     .flatMap(
//                         ([field, value]) => {
//                             const values =
//                                 Array.isArray(value)
//                                     ? value
//                                     : [value];

//                             return values.map(
//                                 (message) =>
//                                     `${field}: ${message}`
//                             );
//                         }
//                     )
//                     .filter(Boolean);

//             if (messages.length) {
//                 return messages.join("\n");
//             }
//         }

//         if (error?.message) {
//             return error.message;
//         }

//         return fallback;
//     };

//     const showCayepAlert = ({
//         type = "info",
//         title = "",
//         text = "",
//         timer = null,
//         showConfirmButton = true,
//         showCancelButton = false,
//         confirmButtonText = "OK",
//         cancelButtonText = "Cancel",
//     }) => {
//         const colors = {
//             success: {
//                 accent: "#4FAE22",
//                 background: "#F4FAEF",
//             },
//             error: {
//                 accent: "#C62828",
//                 background: "#FFF5F5",
//             },
//             warning: {
//                 accent: "#EFA81C",
//                 background: "#FFF9E8",
//             },
//             info: {
//                 accent: "#0F6DAB",
//                 background: "#F2F8FC",
//             },
//         };

//         const selected =
//             colors[type] || colors.info;

//         return Swal.fire({
//             icon: type,
//             title,
//             text,
//             background: selected.background,
//             color: "#142536",
//             showConfirmButton,
//             showCancelButton,
//             confirmButtonText,
//             cancelButtonText,
//             confirmButtonColor: "#062A4B",
//             cancelButtonColor: "#667085",
//             timer: timer || undefined,
//             timerProgressBar: Boolean(timer),
//             reverseButtons: true,
//             buttonsStyling: true,
//             customClass: {
//                 popup: "cayep-edit-swal-popup",
//                 title: "cayep-edit-swal-title",
//                 htmlContainer:
//                     "cayep-edit-swal-text",
//                 confirmButton:
//                     "cayep-edit-swal-confirm",
//                 cancelButton:
//                     "cayep-edit-swal-cancel",
//             },
//             didOpen: (popup) => {
//                 popup.style.borderTop =
//                     `5px solid ${selected.accent}`;

//                 popup.style.boxShadow =
//                     "0 22px 60px rgba(6,42,75,0.22)";
//             },
//         });
//     };

//     const fetchResources = async (
//         courseId
//     ) => {
//         if (!courseId) {
//             setResources([]);
//             return;
//         }

//         try {
//             setLoadingResources(true);

//             const response =
//                 await api.get(
//                     `api/users/subjects/${courseId}/resources/`,
//                     {
//                         headers,
//                     }
//                 );

//             const data =
//                 response.data;

//             if (Array.isArray(data)) {
//                 setResources(data);
//             } else if (
//                 Array.isArray(data?.results)
//             ) {
//                 setResources(
//                     data.results
//                 );
//             } else {
//                 setResources([]);
//             }
//         } catch (error) {
//             console.error(
//                 "Fetch course resources error:",
//                 error
//             );

//             setResources([]);
//         } finally {
//             setLoadingResources(false);
//         }
//     };

//     useEffect(() => {
//         if (!course) {
//             return;
//         }

//         setForm({
//             name: course.name || "",
//             facilitator_name:
//                 course.facilitator_name ||
//                 course.facilitatorName ||
//                 course.instructor_name ||
//                 "",
//             description:
//                 course.description || "",
//             category:
//                 course.category || "",
//             duration:
//                 course.duration || "",
//             lessons:
//                 course.lessons || "",
//             level:
//                 course.level || "",
//             price:
//                 course.price ?? "",
//             old_price:
//                 course.old_price ??
//                 course.oldPrice ??
//                 "",
//         });

//         setImage(null);
//         setVideo(null);
//         setRemoveImage(false);
//         setRemoveVideo(false);

//         setResourceFile(null);
//         setResourceTitle("");
//         setResourceDescription("");

//         fetchResources(course.id);
//     }, [course]);

//     const handleChange = (event) => {
//         const {
//             name,
//             value,
//         } = event.target;

//         setForm((previous) => ({
//             ...previous,
//             [name]: value,
//         }));
//     };

//     const handleImageChange = (
//         event
//     ) => {
//         const file =
//             event.target.files?.[0] ||
//             null;

//         setImage(file);

//         if (file) {
//             setRemoveImage(false);
//         }
//     };

//     const handleVideoChange = (
//         event
//     ) => {
//         const file =
//             event.target.files?.[0] ||
//             null;

//         setVideo(file);

//         if (file) {
//             setRemoveVideo(false);
//         }
//     };

//     const handleResourceChange = (
//         event
//     ) => {
//         const file =
//             event.target.files?.[0] ||
//             null;

//         if (!file) {
//             return;
//         }

//         const extension =
//             getResourceExtension(
//                 file.name
//             );

//         if (
//             !allowedResourceExtensions.includes(
//                 extension
//             )
//         ) {
//             showCayepAlert({
//                 type: "warning",
//                 title: "Unsupported Resource",
//                 text:
//                     "Allowed files are PDF, Word, Excel, PowerPoint, CSV and TXT.",
//             });

//             event.target.value = "";
//             return;
//         }

//         const maxSize =
//             50 * 1024 * 1024;

//         if (file.size > maxSize) {
//             showCayepAlert({
//                 type: "warning",
//                 title: "File Too Large",
//                 text:
//                     "The learning resource must not be larger than 50 MB.",
//             });

//             event.target.value = "";
//             return;
//         }

//         setResourceFile(file);

//         if (!resourceTitle.trim()) {
//             const filename =
//                 file.name.replace(
//                     /\.[^/.]+$/,
//                     ""
//                 );

//             setResourceTitle(
//                 filename
//             );
//         }
//     };

//     const removePendingResource = () => {
//         setResourceFile(null);
//         setResourceTitle("");
//         setResourceDescription("");
//     };

//     const openResource = (
//         resource
//     ) => {
//         const url =
//             getResourceUrl(resource);

//         if (!url) {
//             showCayepAlert({
//                 type: "error",
//                 title: "Resource Unavailable",
//                 text:
//                     "The resource file could not be opened.",
//             });

//             return;
//         }

//         window.open(
//             url,
//             "_blank",
//             "noopener,noreferrer"
//         );
//     };

//     const deleteResource = async (
//         resource
//     ) => {
//         if (
//             !course?.id ||
//             !resource?.id
//         ) {
//             return;
//         }

//         const result =
//             await showCayepAlert({
//                 type: "warning",
//                 title: "Delete Resource?",
//                 text:
//                     `This will permanently remove "${resource.title || resource.original_filename || "this resource"}" from the course.`,
//                 showConfirmButton: true,
//                 showCancelButton: true,
//                 confirmButtonText:
//                     "Yes, Delete",
//                 cancelButtonText:
//                     "Cancel",
//             });

//         if (!result.isConfirmed) {
//             return;
//         }

//         try {
//             await api.delete(
//                 `api/users/subjects/${course.id}/resources/${resource.id}/`,
//                 {
//                     headers,
//                 }
//             );

//             setResources(
//                 (previous) =>
//                     previous.filter(
//                         (item) =>
//                             item.id !==
//                             resource.id
//                     )
//             );

//             showCayepAlert({
//                 type: "success",
//                 title: "Resource Removed",
//                 text:
//                     "The learning resource has been removed.",
//                 timer: 1500,
//                 showConfirmButton: false,
//             });
//         } catch (error) {
//             console.error(
//                 "Delete resource error:",
//                 error
//             );

//             showCayepAlert({
//                 type: "error",
//                 title: "Delete Failed",
//                 text: getErrorMessage(
//                     error,
//                     "Unable to remove this learning resource."
//                 ),
//             });
//         }
//     };

//     const uploadPendingResource = async (
//         courseId
//     ) => {
//         if (
//             !courseId ||
//             !resourceFile
//         ) {
//             return null;
//         }

//         const formData =
//             new FormData();

//         formData.append(
//             "file",
//             resourceFile,
//             resourceFile.name
//         );

//         formData.append(
//             "title",
//             resourceTitle.trim() ||
//                 resourceFile.name.replace(
//                     /\.[^/.]+$/,
//                     ""
//                 )
//         );

//         formData.append(
//             "description",
//             resourceDescription.trim()
//         );

//         const response =
//             await api.post(
//                 `api/users/subjects/${courseId}/resources/`,
//                 formData,
//                 {
//                     headers: {
//                         ...headers,
//                         "Content-Type":
//                             "multipart/form-data",
//                     },
//                 }
//             );

//         return response.data;
//     };

//     const updateCourse = async (
//         event
//     ) => {
//         event.preventDefault();

//         if (!course?.id) {
//             return;
//         }

//         if (!form.name.trim()) {
//             showCayepAlert({
//                 type: "warning",
//                 title: "Course Name Required",
//                 text:
//                     "Please enter the course name.",
//             });

//             return;
//         }

//         if (
//             !form.facilitator_name.trim()
//         ) {
//             showCayepAlert({
//                 type: "warning",
//                 title: "Facilitator Required",
//                 text:
//                     "Please enter the facilitator or instructor name.",
//             });

//             return;
//         }

//         try {
//             setSaving(true);

//             const formData =
//                 new FormData();

//             formData.append(
//                 "name",
//                 form.name.trim()
//             );

//             formData.append(
//                 "facilitator_name",
//                 form.facilitator_name.trim()
//             );

//             formData.append(
//                 "description",
//                 form.description.trim()
//             );

//             formData.append(
//                 "category",
//                 form.category.trim()
//             );

//             formData.append(
//                 "duration",
//                 form.duration.trim()
//             );

//             formData.append(
//                 "lessons",
//                 form.lessons.trim()
//             );

//             formData.append(
//                 "level",
//                 form.level.trim()
//             );

//             formData.append(
//                 "price",
//                 String(form.price ?? "").trim()
//             );

//             formData.append(
//                 "old_price",
//                 String(
//                     form.old_price ?? ""
//                 ).trim()
//             );

//             if (image) {
//                 formData.append(
//                     "image",
//                     image,
//                     image.name
//                 );
//             } else if (
//                 removeImage
//             ) {
//                 formData.append(
//                     "image",
//                     ""
//                 );
//             }

//             if (video) {
//                 formData.append(
//                     "video",
//                     video,
//                     video.name
//                 );
//             } else if (
//                 removeVideo
//             ) {
//                 formData.append(
//                     "video",
//                     ""
//                 );
//             }

//             const response =
//                 await api.patch(
//                     `api/users/subjects/${course.id}/`,
//                     formData,
//                     {
//                         headers: {
//                             ...headers,
//                             "Content-Type":
//                                 "multipart/form-data",
//                         },
//                     }
//                 );

//             const updatedCourse =
//                 response.data;

//             let resourceUploadFailed =
//                 false;

//             if (resourceFile) {
//                 try {
//                     await uploadPendingResource(
//                         course.id
//                     );

//                     await fetchResources(
//                         course.id
//                     );
//                 } catch (
//                     resourceError
//                 ) {
//                     resourceUploadFailed =
//                         true;

//                     console.error(
//                         "Course updated but resource upload failed:",
//                         resourceError
//                     );
//                 }
//             }

//             if (onUpdated) {
//                 onUpdated(
//                     updatedCourse
//                 );
//             }

//             if (
//                 resourceUploadFailed
//             ) {
//                 showCayepAlert({
//                     type: "warning",
//                     title: "Course Updated",
//                     text:
//                         "The course was updated successfully, but the selected learning resource could not be uploaded. You can try adding the resource again.",
//                 });

//                 setResourceFile(null);
//                 setResourceTitle("");
//                 setResourceDescription("");

//                 return;
//             }

//             showCayepAlert({
//                 type: "success",
//                 title: "Course Updated",
//                 text: resourceFile
//                     ? "The course and learning resource have been updated successfully."
//                     : "The course has been updated successfully.",
//                 timer: 1800,
//                 showConfirmButton: false,
//             });

//             setResourceFile(null);
//             setResourceTitle("");
//             setResourceDescription("");
//         } catch (error) {
//             console.error(
//                 "Update course error:",
//                 error
//             );

//             const errorMessage =
//                 getErrorMessage(
//                     error,
//                     "Unable to update the course. Please try again."
//                 );

//             showCayepAlert({
//                 type: "error",
//                 title: "Update Failed",
//                 text: errorMessage,
//             });
//         } finally {
//             setSaving(false);
//         }
//     };

//     const confirmDelete = async () => {
//         if (!course?.id) {
//             return;
//         }

//         const result =
//             await showCayepAlert({
//                 type: "warning",
//                 title: "Delete Course?",
//                 text:
//                     `This will permanently delete "${course.name || "this course"}" and its associated course record. This action cannot be undone.`,
//                 showConfirmButton: true,
//                 showCancelButton: true,
//                 confirmButtonText:
//                     "Yes, Delete Course",
//                 cancelButtonText:
//                     "Cancel",
//             });

//         if (!result.isConfirmed) {
//             return;
//         }

//         try {
//             setDeleting(true);

//             await api.delete(
//                 `api/users/subjects/${course.id}/`,
//                 {
//                     headers,
//                 }
//             );

//             await showCayepAlert({
//                 type: "success",
//                 title: "Course Deleted",
//                 text:
//                     "The course has been permanently deleted.",
//                 timer: 1700,
//                 showConfirmButton: false,
//             });

//             if (onDeleted) {
//                 await onDeleted(
//                     course.id
//                 );
//             }
//         } catch (error) {
//             console.error(
//                 "Delete course error:",
//                 error
//             );

//             showCayepAlert({
//                 type: "error",
//                 title: "Delete Failed",
//                 text: getErrorMessage(
//                     error,
//                     "Unable to delete this course. Please try again."
//                 ),
//             });
//         } finally {
//             setDeleting(false);
//         }
//     };

//     if (!course) {
//         return null;
//     }

//     return (
//         <div
//             className="cayep-course-edit-overlay"
//             role="dialog"
//             aria-modal="true"
//             aria-labelledby="cayep-edit-course-title"
//         >
//             <div
//                 className="cayep-course-edit-modal"
//                 onClick={(event) =>
//                     event.stopPropagation()
//                 }
//             >
//                 <div className="cayep-course-edit-header">
//                     <div>
//                         <span>
//                             COURSE MANAGEMENT
//                         </span>

//                         <h2 id="cayep-edit-course-title">
//                             Edit Course
//                         </h2>

//                         <p>
//                             Update course information,
//                             media and learning resources.
//                         </p>
//                     </div>

//                     <button
//                         type="button"
//                         className="cayep-course-edit-close"
//                         onClick={onClose}
//                         disabled={
//                             saving ||
//                             deleting
//                         }
//                         aria-label="Close edit course"
//                     >
//                         ×
//                     </button>
//                 </div>

//                 <form
//                     onSubmit={
//                         updateCourse
//                     }
//                 >
//                     <div className="cayep-course-edit-body">
//                         <div className="cayep-course-edit-section">
//                             <div className="cayep-course-edit-section-title">
//                                 <FaBookOpen />

//                                 <div>
//                                     <h3>
//                                         Course Information
//                                     </h3>

//                                     <p>
//                                         Update the information
//                                         displayed to learners.
//                                     </p>
//                                 </div>
//                             </div>

//                             <div className="cayep-course-edit-grid">
//                                 <label className="cayep-course-edit-field full">
//                                     <span>
//                                         Course Name
//                                     </span>

//                                     <input
//                                         type="text"
//                                         name="name"
//                                         value={
//                                             form.name
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         maxLength={
//                                             100
//                                         }
//                                         required
//                                         placeholder="Enter course name"
//                                     />
//                                 </label>

//                                 <label className="cayep-course-edit-field full">
//                                     <span>
//                                         Facilitator / Instructor
//                                     </span>

//                                     <div className="cayep-facilitator-field">
//                                         <FaEdit />

//                                         <input
//                                             type="text"
//                                             name="facilitator_name"
//                                             value={
//                                                 form.facilitator_name
//                                             }
//                                             onChange={
//                                                 handleChange
//                                             }
//                                             maxLength={
//                                                 150
//                                             }
//                                             required
//                                             placeholder="Enter facilitator or instructor name"
//                                         />
//                                     </div>
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Category
//                                     </span>

//                                     <input
//                                         type="text"
//                                         name="category"
//                                         value={
//                                             form.category
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         maxLength={
//                                             100
//                                         }
//                                         placeholder="e.g. Leadership"
//                                     />
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Level
//                                     </span>

//                                     <input
//                                         type="text"
//                                         name="level"
//                                         value={
//                                             form.level
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         maxLength={
//                                             50
//                                         }
//                                         placeholder="e.g. Beginner"
//                                     />
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Duration
//                                     </span>

//                                     <div className="cayep-course-input-icon">
//                                         <FaClock />

//                                         <input
//                                             type="text"
//                                             name="duration"
//                                             value={
//                                                 form.duration
//                                             }
//                                             onChange={
//                                                 handleChange
//                                             }
//                                             maxLength={
//                                                 100
//                                             }
//                                             placeholder="e.g. 6 weeks"
//                                         />
//                                     </div>
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Lessons
//                                     </span>

//                                     <div className="cayep-course-input-icon">
//                                         <FaLayerGroup />

//                                         <input
//                                             type="text"
//                                             name="lessons"
//                                             value={
//                                                 form.lessons
//                                             }
//                                             onChange={
//                                                 handleChange
//                                             }
//                                             maxLength={
//                                                 100
//                                             }
//                                             placeholder="e.g. 12 lessons"
//                                         />
//                                     </div>
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Current Price
//                                     </span>

//                                     <input
//                                         type="text"
//                                         name="price"
//                                         value={
//                                             form.price
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         maxLength={
//                                             50
//                                         }
//                                         placeholder="e.g. $150"
//                                     />
//                                 </label>

//                                 <label className="cayep-course-edit-field">
//                                     <span>
//                                         Old Price
//                                     </span>

//                                     <input
//                                         type="text"
//                                         name="old_price"
//                                         value={
//                                             form.old_price
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         maxLength={
//                                             50
//                                         }
//                                         placeholder="e.g. $200"
//                                     />
//                                 </label>

//                                 <label className="cayep-course-edit-field full">
//                                     <span>
//                                         Description
//                                     </span>

//                                     <textarea
//                                         name="description"
//                                         value={
//                                             form.description
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         rows={
//                                             6
//                                         }
//                                         placeholder="Describe this course..."
//                                     />
//                                 </label>
//                             </div>
//                         </div>

//                         <div className="cayep-course-edit-section">
//                             <div className="cayep-course-edit-section-title">
//                                 <FaImage />

//                                 <div>
//                                     <h3>
//                                         Course Thumbnail
//                                     </h3>

//                                     <p>
//                                         Replace or remove the
//                                         existing course image.
//                                     </p>
//                                 </div>
//                             </div>

//                             <div className="cayep-course-media-box">
//                                 <div className="cayep-course-current-media">
//                                     {course.image &&
//                                     !removeImage &&
//                                     !image ? (
//                                         <img
//                                             src={getMediaUrl(
//                                                 course.image
//                                             )}
//                                             alt={
//                                                 course.name
//                                             }
//                                         />
//                                     ) : image ? (
//                                         <img
//                                             src={URL.createObjectURL(
//                                                 image
//                                             )}
//                                             alt="New course preview"
//                                         />
//                                     ) : (
//                                         <FaImage />
//                                     )}
//                                 </div>

//                                 <div className="cayep-course-media-actions">
//                                     <label className="cayep-upload-button">
//                                         <FaUpload />

//                                         {image
//                                             ? "Change Image"
//                                             : "Upload New Image"}

//                                         <input
//                                             type="file"
//                                             accept="image/*"
//                                             onChange={
//                                                 handleImageChange
//                                             }
//                                         />
//                                     </label>

//                                     {course.image &&
//                                         !removeImage && (
//                                             <button
//                                                 type="button"
//                                                 className="cayep-remove-media"
//                                                 onClick={() =>
//                                                     setRemoveImage(
//                                                         true
//                                                     )
//                                                 }
//                                             >
//                                                 <FaTrash />
//                                                 Remove Image
//                                             </button>
//                                         )}

//                                     {removeImage && (
//                                         <button
//                                             type="button"
//                                             className="cayep-restore-media"
//                                             onClick={() =>
//                                                 setRemoveImage(
//                                                     false
//                                                 )
//                                             }
//                                         >
//                                             Restore Existing
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="cayep-course-edit-section">
//                             <div className="cayep-course-edit-section-title">
//                                 <FaVideo />

//                                 <div>
//                                     <h3>
//                                         Course Video
//                                     </h3>

//                                     <p>
//                                         Replace or remove the
//                                         published course video.
//                                     </p>
//                                 </div>
//                             </div>

//                             <div className="cayep-course-video-box">
//                                 <div className="cayep-course-video-status">
//                                     <FaVideo />

//                                     <div>
//                                         <strong>
//                                             {course.video &&
//                                             !removeVideo
//                                                 ? "Video Published"
//                                                 : "No Published Video"}
//                                         </strong>

//                                         <small>
//                                             {video
//                                                 ? video.name
//                                                 : course.video &&
//                                                   !removeVideo
//                                                 ? "An existing course video is attached."
//                                                 : "Upload a new video when needed."}
//                                         </small>
//                                     </div>
//                                 </div>

//                                 <div className="cayep-course-media-actions">
//                                     <label className="cayep-upload-button">
//                                         <FaUpload />

//                                         {video
//                                             ? "Change Video"
//                                             : "Upload New Video"}

//                                         <input
//                                             type="file"
//                                             accept="video/*"
//                                             onChange={
//                                                 handleVideoChange
//                                             }
//                                         />
//                                     </label>

//                                     {course.video &&
//                                         !removeVideo && (
//                                             <button
//                                                 type="button"
//                                                 className="cayep-remove-media"
//                                                 onClick={() =>
//                                                     setRemoveVideo(
//                                                         true
//                                                     )
//                                                 }
//                                             >
//                                                 <FaTrash />
//                                                 Remove Video
//                                             </button>
//                                         )}

//                                     {removeVideo && (
//                                         <button
//                                             type="button"
//                                             className="cayep-restore-media"
//                                             onClick={() =>
//                                                 setRemoveVideo(
//                                                     false
//                                                 )
//                                             }
//                                         >
//                                             Keep Existing Video
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="cayep-course-edit-section">
//                             <div className="cayep-course-edit-section-title">
//                                 <FaPaperclip />

//                                 <div>
//                                     <h3>
//                                         Learning Resources
//                                     </h3>

//                                     <p>
//                                         Add PDF, Word, Excel,
//                                         PowerPoint, CSV or TXT
//                                         learning materials.
//                                     </p>
//                                 </div>
//                             </div>

//                             <div className="cayep-resource-upload-panel">
//                                 <div className="cayep-resource-upload-top">
//                                     <label className="cayep-resource-upload-button">
//                                         <FaUpload />

//                                         Select Resource

//                                         <input
//                                             type="file"
//                                             accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt"
//                                             onChange={
//                                                 handleResourceChange
//                                             }
//                                         />
//                                     </label>

//                                     <span>
//                                         Maximum 50 MB
//                                     </span>
//                                 </div>

//                                 {resourceFile && (
//                                     <div className="cayep-pending-resource">
//                                         <div className="cayep-pending-resource-icon">
//                                             {getResourceIcon(
//                                                 resourceFile.name
//                                             )}
//                                         </div>

//                                         <div className="cayep-pending-resource-info">
//                                             <strong>
//                                                 {resourceFile.name}
//                                             </strong>

//                                             <span>
//                                                 {formatFileSize(
//                                                     resourceFile.size
//                                                 )}
//                                             </span>
//                                         </div>

//                                         <button
//                                             type="button"
//                                             className="cayep-resource-remove-pending"
//                                             onClick={
//                                                 removePendingResource
//                                             }
//                                             disabled={
//                                                 saving
//                                             }
//                                         >
//                                             <FaTrash />
//                                             Remove
//                                         </button>
//                                     </div>
//                                 )}

//                                 {resourceFile && (
//                                     <div className="cayep-resource-details-grid">
//                                         <label>
//                                             <span>
//                                                 Resource Title
//                                             </span>

//                                             <input
//                                                 type="text"
//                                                 value={
//                                                     resourceTitle
//                                                 }
//                                                 onChange={(
//                                                     event
//                                                 ) =>
//                                                     setResourceTitle(
//                                                         event
//                                                             .target
//                                                             .value
//                                                     )
//                                                 }
//                                                 maxLength={
//                                                     200
//                                                 }
//                                                 placeholder="Enter resource title"
//                                             />
//                                         </label>

//                                         <label>
//                                             <span>
//                                                 Description
//                                             </span>

//                                             <textarea
//                                                 value={
//                                                     resourceDescription
//                                                 }
//                                                 onChange={(
//                                                     event
//                                                 ) =>
//                                                     setResourceDescription(
//                                                         event
//                                                             .target
//                                                             .value
//                                                     )
//                                                 }
//                                                 rows={
//                                                     3
//                                                 }
//                                                 placeholder="Optional description for this resource"
//                                             />
//                                         </label>
//                                     </div>
//                                 )}

//                                 <div className="cayep-resource-save-notice">
//                                     <FaSave />

//                                     <span>
//                                         {resourceFile
//                                             ? "This resource will be uploaded when you click Save Changes."
//                                             : "Select a resource above. It will be uploaded together with the course update."}
//                                     </span>
//                                 </div>
//                             </div>

//                             <div className="cayep-existing-resources">
//                                 <div className="cayep-existing-resources-header">
//                                     <div>
//                                         <strong>
//                                             Existing Learning Resources
//                                         </strong>

//                                         <small>
//                                             Resources already
//                                             attached to this course
//                                         </small>
//                                     </div>

//                                     <span>
//                                         {resources.length}
//                                     </span>
//                                 </div>

//                                 {loadingResources ? (
//                                     <div className="cayep-resource-empty">
//                                         Loading resources...
//                                     </div>
//                                 ) : resources.length ===
//                                   0 ? (
//                                     <div className="cayep-resource-empty">
//                                         <FaPaperclip />

//                                         <span>
//                                             No learning resources
//                                             have been added to
//                                             this course yet.
//                                         </span>
//                                     </div>
//                                 ) : (
//                                     <div className="cayep-resource-list">
//                                         {resources.map(
//                                             (
//                                                 resource
//                                             ) => (
//                                                 <div
//                                                     className="cayep-existing-resource"
//                                                     key={
//                                                         resource.id
//                                                     }
//                                                 >
//                                                     <div className="cayep-existing-resource-icon">
//                                                         {getResourceIcon(
//                                                             resource.original_filename ||
//                                                                 resource.file_name ||
//                                                                 resource.name ||
//                                                                 resource.file
//                                                         )}
//                                                     </div>

//                                                     <div className="cayep-existing-resource-info">
//                                                         <strong>
//                                                             {resource.title ||
//                                                                 resource.original_filename ||
//                                                                 resource.file_name ||
//                                                                 "Learning Resource"}
//                                                         </strong>

//                                                         <span>
//                                                             {resource.description ||
//                                                                 "Course learning material"}
//                                                         </span>
//                                                     </div>

//                                                     <div className="cayep-existing-resource-actions">
//                                                         <button
//                                                             type="button"
//                                                             onClick={() =>
//                                                                 openResource(
//                                                                     resource
//                                                                 )
//                                                             }
//                                                             disabled={
//                                                                 saving
//                                                             }
//                                                             title="Open resource"
//                                                         >
//                                                             <FaDownload />
//                                                         </button>

//                                                         <button
//                                                             type="button"
//                                                             className="danger"
//                                                             onClick={() =>
//                                                                 deleteResource(
//                                                                     resource
//                                                                 )
//                                                             }
//                                                             disabled={
//                                                                 saving
//                                                             }
//                                                             title="Delete resource"
//                                                         >
//                                                             <FaTrash />
//                                                         </button>
//                                                     </div>
//                                                 </div>
//                                             )
//                                         )}
//                                     </div>
//                                 )}
//                             </div>
//                         </div>
//                     </div>

//                     <div className="cayep-course-edit-footer">
//                         <button
//                             type="button"
//                             className="cayep-course-delete"
//                             onClick={
//                                 confirmDelete
//                             }
//                             disabled={
//                                 saving ||
//                                 deleting
//                             }
//                         >
//                             <FaTrash />

//                             {deleting
//                                 ? "Deleting..."
//                                 : "Delete Course"}
//                         </button>

//                         <div className="cayep-course-edit-footer-right">
//                             <button
//                                 type="button"
//                                 className="cayep-course-cancel"
//                                 onClick={
//                                     onClose
//                                 }
//                                 disabled={
//                                     saving ||
//                                     deleting
//                                 }
//                             >
//                                 Cancel
//                             </button>

//                             <button
//                                 type="submit"
//                                 className="cayep-course-save"
//                                 disabled={
//                                     saving ||
//                                     deleting
//                                 }
//                             >
//                                 <FaSave />

//                                 {saving
//                                     ? "Saving Course..."
//                                     : "Save Changes"}
//                             </button>
//                         </div>
//                     </div>
//                 </form>
//             </div>

//             <style>{`
//                 .cayep-course-edit-overlay {
//                     position: fixed;
//                     inset: 0;
//                     z-index: 9999;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     padding: 25px;
//                     box-sizing: border-box;
//                     background: rgba(2, 8, 13, 0.82);
//                     backdrop-filter: blur(7px);
//                     overflow-y: auto;
//                 }

//                 .cayep-course-edit-modal {
//                     width: min(100%, 980px);
//                     max-height: calc(100vh - 50px);
//                     overflow: hidden;
//                     border: 1px solid rgba(246, 201, 68, 0.18);
//                     background: #081722;
//                     box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
//                     color: #F0EDEA;
//                 }

//                 .cayep-course-edit-header {
//                     display: flex;
//                     align-items: flex-start;
//                     justify-content: space-between;
//                     gap: 20px;
//                     padding: 25px 28px;
//                     border-bottom: 1px solid rgba(240, 237, 234, 0.08);
//                     background: linear-gradient(
//                         135deg,
//                         rgba(6, 42, 75, 0.95),
//                         rgba(7, 19, 29, 0.98)
//                     );
//                 }

//                 .cayep-course-edit-header span {
//                     color: #F6C944;
//                     font-size: 8px;
//                     font-weight: 800;
//                     letter-spacing: 2.5px;
//                 }

//                 .cayep-course-edit-header h2 {
//                     margin: 6px 0;
//                     font-size: 24px;
//                 }

//                 .cayep-course-edit-header p {
//                     margin: 0;
//                     color: #87939A;
//                     font-size: 11px;
//                 }

//                 .cayep-course-edit-close {
//                     width: 38px;
//                     height: 38px;
//                     flex-shrink: 0;
//                     border: 1px solid rgba(240, 237, 234, 0.1);
//                     background: rgba(255, 255, 255, 0.04);
//                     color: #A9B1B5;
//                     cursor: pointer;
//                     font-size: 24px;
//                     line-height: 1;
//                 }

//                 .cayep-course-edit-close:hover {
//                     color: #F6C944;
//                     border-color: rgba(246, 201, 68, 0.4);
//                 }

//                 .cayep-course-edit-body {
//                     max-height: calc(100vh - 220px);
//                     overflow-y: auto;
//                     padding: 25px 28px;
//                 }

//                 .cayep-course-edit-body::-webkit-scrollbar {
//                     width: 7px;
//                 }

//                 .cayep-course-edit-body::-webkit-scrollbar-track {
//                     background: rgba(255,255,255,0.025);
//                 }

//                 .cayep-course-edit-body::-webkit-scrollbar-thumb {
//                     background: rgba(246,201,68,0.35);
//                     border-radius: 20px;
//                 }

//                 .cayep-course-edit-section {
//                     margin-bottom: 25px;
//                     padding: 21px;
//                     border: 1px solid rgba(240, 237, 234, 0.08);
//                     background: rgba(255, 255, 255, 0.018);
//                 }

//                 .cayep-course-edit-section:last-child {
//                     margin-bottom: 0;
//                 }

//                 .cayep-course-edit-section-title {
//                     display: flex;
//                     gap: 12px;
//                     margin-bottom: 20px;
//                 }

//                 .cayep-course-edit-section-title > svg {
//                     flex-shrink: 0;
//                     margin-top: 2px;
//                     color: #F6C944;
//                     font-size: 19px;
//                 }

//                 .cayep-course-edit-section-title h3 {
//                     margin: 0;
//                     font-size: 15px;
//                 }

//                 .cayep-course-edit-section-title p {
//                     margin: 5px 0 0;
//                     color: #758188;
//                     font-size: 10px;
//                 }

//                 .cayep-course-edit-grid {
//                     display: grid;
//                     grid-template-columns: repeat(2, minmax(0, 1fr));
//                     gap: 17px;
//                 }

//                 .cayep-course-edit-field {
//                     display: flex;
//                     flex-direction: column;
//                     gap: 7px;
//                     min-width: 0;
//                 }

//                 .cayep-course-edit-field.full {
//                     grid-column: 1 / -1;
//                 }

//                 .cayep-course-edit-field > span,
//                 .cayep-resource-details-grid label > span {
//                     color: #A8B0B5;
//                     font-size: 9px;
//                     font-weight: 700;
//                     letter-spacing: 0.4px;
//                 }

//                 .cayep-course-edit-field input,
//                 .cayep-course-edit-field textarea,
//                 .cayep-facilitator-field,
//                 .cayep-resource-details-grid input,
//                 .cayep-resource-details-grid textarea {
//                     width: 100%;
//                     box-sizing: border-box;
//                     border: 1px solid rgba(240, 237, 234, 0.11);
//                     outline: none;
//                     background: rgba(0, 0, 0, 0.18);
//                     color: #F0EDEA;
//                     font-family: inherit;
//                     font-size: 11px;
//                     transition: border-color 0.2s ease, background 0.2s ease;
//                 }

//                 .cayep-course-edit-field input,
//                 .cayep-course-edit-field textarea {
//                     padding: 12px 13px;
//                 }

//                 .cayep-course-edit-field textarea {
//                     resize: vertical;
//                     min-height: 110px;
//                     line-height: 1.7;
//                 }

//                 .cayep-course-edit-field input:focus,
//                 .cayep-course-edit-field textarea:focus,
//                 .cayep-facilitator-field:focus-within,
//                 .cayep-resource-details-grid input:focus,
//                 .cayep-resource-details-grid textarea:focus {
//                     border-color: rgba(246, 201, 68, 0.7);
//                     background: rgba(246, 201, 68, 0.025);
//                 }

//                 .cayep-facilitator-field {
//                     display: flex;
//                     align-items: center;
//                     gap: 10px;
//                     padding-left: 12px;
//                 }

//                 .cayep-facilitator-field svg {
//                     flex-shrink: 0;
//                     color: #F6C944;
//                 }

//                 .cayep-facilitator-field input {
//                     border: 0;
//                     padding: 12px 13px 12px 0;
//                     background: transparent;
//                 }

//                 .cayep-facilitator-field input:focus {
//                     background: transparent;
//                 }

//                 .cayep-course-input-icon {
//                     position: relative;
//                     display: flex;
//                     align-items: center;
//                 }

//                 .cayep-course-input-icon > svg {
//                     position: absolute;
//                     left: 13px;
//                     color: #F6C944;
//                     font-size: 11px;
//                     pointer-events: none;
//                 }

//                 .cayep-course-input-icon input {
//                     padding-left: 34px;
//                 }

//                 .cayep-course-media-box,
//                 .cayep-course-video-box {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 20px;
//                     padding: 15px;
//                     border: 1px solid rgba(240, 237, 234, 0.07);
//                     background: rgba(0, 0, 0, 0.13);
//                 }

//                 .cayep-course-current-media {
//                     width: 180px;
//                     height: 105px;
//                     flex-shrink: 0;
//                     display: grid;
//                     place-items: center;
//                     overflow: hidden;
//                     background: #063A6B;
//                     color: #F6C944;
//                 }

//                 .cayep-course-current-media img {
//                     width: 100%;
//                     height: 100%;
//                     object-fit: cover;
//                 }

//                 .cayep-course-current-media svg {
//                     font-size: 30px;
//                 }

//                 .cayep-course-media-actions {
//                     display: flex;
//                     align-items: center;
//                     justify-content: flex-end;
//                     gap: 9px;
//                     flex-wrap: wrap;
//                 }

//                 .cayep-upload-button,
//                 .cayep-remove-media,
//                 .cayep-restore-media {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 7px;
//                     min-height: 36px;
//                     box-sizing: border-box;
//                     padding: 0 12px;
//                     border: 1px solid rgba(240, 237, 234, 0.12);
//                     cursor: pointer;
//                     font-family: inherit;
//                     font-size: 9px;
//                     font-weight: 700;
//                 }

//                 .cayep-upload-button {
//                     position: relative;
//                     overflow: hidden;
//                     border-color: rgba(246, 201, 68, 0.45);
//                     background: rgba(246, 201, 68, 0.08);
//                     color: #F6C944;
//                 }

//                 .cayep-upload-button input,
//                 .cayep-resource-upload-button input {
//                     position: absolute;
//                     inset: 0;
//                     width: 100%;
//                     height: 100%;
//                     opacity: 0;
//                     cursor: pointer;
//                 }

//                 .cayep-remove-media {
//                     border-color: rgba(180, 35, 24, 0.3);
//                     background: rgba(180, 35, 24, 0.08);
//                     color: #F28B82;
//                 }

//                 .cayep-restore-media {
//                     border-color: rgba(79, 174, 34, 0.3);
//                     background: rgba(79, 174, 34, 0.06);
//                     color: #8FD06E;
//                 }

//                 .cayep-upload-button:hover,
//                 .cayep-remove-media:hover,
//                 .cayep-restore-media:hover {
//                     filter: brightness(1.15);
//                 }

//                 .cayep-course-video-status {
//                     display: flex;
//                     align-items: center;
//                     gap: 13px;
//                     min-width: 0;
//                 }

//                 .cayep-course-video-status > svg {
//                     flex-shrink: 0;
//                     color: #F6C944;
//                     font-size: 22px;
//                 }

//                 .cayep-course-video-status strong {
//                     display: block;
//                     color: #F0EDEA;
//                     font-size: 11px;
//                 }

//                 .cayep-course-video-status small {
//                     display: block;
//                     max-width: 400px;
//                     margin-top: 4px;
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                     white-space: nowrap;
//                     color: #707C82;
//                     font-size: 9px;
//                 }

//                 .cayep-resource-upload-panel {
//                     padding: 16px;
//                     border: 1px solid rgba(246, 201, 68, 0.13);
//                     background: rgba(6, 42, 75, 0.12);
//                 }

//                 .cayep-resource-upload-top {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 12px;
//                     margin-bottom: 12px;
//                 }

//                 .cayep-resource-upload-top > span {
//                     color: #707C82;
//                     font-size: 9px;
//                 }

//                 .cayep-resource-upload-button {
//                     position: relative;
//                     overflow: hidden;
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 8px;
//                     min-height: 38px;
//                     padding: 0 14px;
//                     border: 1px solid rgba(246, 201, 68, 0.45);
//                     background: rgba(246, 201, 68, 0.08);
//                     color: #F6C944;
//                     cursor: pointer;
//                     font-size: 9px;
//                     font-weight: 800;
//                 }

//                 .cayep-pending-resource {
//                     display: flex;
//                     align-items: center;
//                     gap: 12px;
//                     padding: 11px;
//                     border: 1px solid rgba(79, 174, 34, 0.25);
//                     background: rgba(79, 174, 34, 0.055);
//                 }

//                 .cayep-pending-resource-icon {
//                     width: 40px;
//                     height: 40px;
//                     flex-shrink: 0;
//                     display: grid;
//                     place-items: center;
//                     background: rgba(246, 201, 68, 0.1);
//                     color: #F6C944;
//                     font-size: 19px;
//                 }

//                 .cayep-pending-resource-info {
//                     flex: 1;
//                     min-width: 0;
//                     display: flex;
//                     flex-direction: column;
//                     gap: 4px;
//                 }

//                 .cayep-pending-resource-info strong {
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                     white-space: nowrap;
//                     color: #F0EDEA;
//                     font-size: 10px;
//                 }

//                 .cayep-pending-resource-info span {
//                     color: #8C989E;
//                     font-size: 9px;
//                 }

//                 .cayep-resource-remove-pending {
//                     display: inline-flex;
//                     align-items: center;
//                     gap: 6px;
//                     border: 0;
//                     background: transparent;
//                     color: #F28B82;
//                     cursor: pointer;
//                     font-family: inherit;
//                     font-size: 9px;
//                     font-weight: 700;
//                 }

//                 .cayep-resource-details-grid {
//                     display: grid;
//                     grid-template-columns: 1fr 1fr;
//                     gap: 12px;
//                     margin-top: 12px;
//                 }

//                 .cayep-resource-details-grid label {
//                     display: flex;
//                     flex-direction: column;
//                     gap: 7px;
//                 }

//                 .cayep-resource-details-grid input,
//                 .cayep-resource-details-grid textarea {
//                     padding: 10px 11px;
//                 }

//                 .cayep-resource-details-grid textarea {
//                     resize: vertical;
//                     min-height: 74px;
//                 }

//                 .cayep-resource-save-notice {
//                     display: flex;
//                     align-items: center;
//                     gap: 8px;
//                     margin-top: 12px;
//                     color: #849197;
//                     font-size: 9px;
//                     line-height: 1.5;
//                 }

//                 .cayep-resource-save-notice svg {
//                     flex-shrink: 0;
//                     color: #F6C944;
//                 }

//                 .cayep-existing-resources {
//                     margin-top: 15px;
//                     border: 1px solid rgba(240, 237, 234, 0.07);
//                     background: rgba(0, 0, 0, 0.13);
//                 }

//                 .cayep-existing-resources-header {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 15px;
//                     padding: 13px 14px;
//                     border-bottom: 1px solid rgba(240, 237, 234, 0.07);
//                 }

//                 .cayep-existing-resources-header strong {
//                     display: block;
//                     color: #F0EDEA;
//                     font-size: 10px;
//                 }

//                 .cayep-existing-resources-header small {
//                     display: block;
//                     margin-top: 4px;
//                     color: #6F7B81;
//                     font-size: 8px;
//                 }

//                 .cayep-existing-resources-header > span {
//                     min-width: 25px;
//                     height: 25px;
//                     display: grid;
//                     place-items: center;
//                     border-radius: 50%;
//                     background: rgba(246, 201, 68, 0.1);
//                     color: #F6C944;
//                     font-size: 9px;
//                     font-weight: 800;
//                 }

//                 .cayep-resource-list {
//                     display: flex;
//                     flex-direction: column;
//                 }

//                 .cayep-existing-resource {
//                     display: flex;
//                     align-items: center;
//                     gap: 11px;
//                     padding: 11px 14px;
//                     border-bottom: 1px solid rgba(240, 237, 234, 0.05);
//                 }

//                 .cayep-existing-resource:last-child {
//                     border-bottom: 0;
//                 }

//                 .cayep-existing-resource-icon {
//                     width: 35px;
//                     height: 35px;
//                     flex-shrink: 0;
//                     display: grid;
//                     place-items: center;
//                     background: rgba(246, 201, 68, 0.08);
//                     color: #F6C944;
//                     font-size: 16px;
//                 }

//                 .cayep-existing-resource-info {
//                     flex: 1;
//                     min-width: 0;
//                 }

//                 .cayep-existing-resource-info strong {
//                     display: block;
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                     white-space: nowrap;
//                     color: #E6EAEC;
//                     font-size: 10px;
//                 }

//                 .cayep-existing-resource-info span {
//                     display: block;
//                     margin-top: 4px;
//                     overflow: hidden;
//                     text-overflow: ellipsis;
//                     white-space: nowrap;
//                     color: #69767D;
//                     font-size: 8px;
//                 }

//                 .cayep-existing-resource-actions {
//                     display: flex;
//                     gap: 6px;
//                 }

//                 .cayep-existing-resource-actions button {
//                     width: 30px;
//                     height: 30px;
//                     display: grid;
//                     place-items: center;
//                     border: 1px solid rgba(240, 237, 234, 0.1);
//                     background: rgba(255,255,255,0.035);
//                     color: #F6C944;
//                     cursor: pointer;
//                 }

//                 .cayep-existing-resource-actions button.danger {
//                     color: #F28B82;
//                 }

//                 .cayep-existing-resource-actions button:hover {
//                     background: rgba(246, 201, 68, 0.08);
//                 }

//                 .cayep-existing-resource-actions button.danger:hover {
//                     background: rgba(180, 35, 24, 0.1);
//                 }

//                 .cayep-existing-resource-actions button:disabled {
//                     opacity: 0.4;
//                     cursor: not-allowed;
//                 }

//                 .cayep-resource-empty {
//                     min-height: 75px;
//                     display: flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 8px;
//                     padding: 15px;
//                     color: #69767D;
//                     font-size: 9px;
//                     text-align: center;
//                 }

//                 .cayep-resource-empty svg {
//                     color: #F6C944;
//                 }

//                 .cayep-course-edit-footer {
//                     display: flex;
//                     align-items: center;
//                     justify-content: space-between;
//                     gap: 15px;
//                     padding: 17px 28px;
//                     border-top: 1px solid rgba(240, 237, 234, 0.08);
//                     background: rgba(0, 0, 0, 0.2);
//                 }

//                 .cayep-course-edit-footer-right {
//                     display: flex;
//                     align-items: center;
//                     gap: 9px;
//                 }

//                 .cayep-course-delete,
//                 .cayep-course-cancel,
//                 .cayep-course-save {
//                     display: inline-flex;
//                     align-items: center;
//                     justify-content: center;
//                     gap: 8px;
//                     min-height: 40px;
//                     padding: 0 16px;
//                     cursor: pointer;
//                     font-family: inherit;
//                     font-size: 10px;
//                     font-weight: 800;
//                 }

//                 .cayep-course-delete {
//                     border: 1px solid rgba(180, 35, 24, 0.35);
//                     background: rgba(180, 35, 24, 0.08);
//                     color: #F28B82;
//                 }

//                 .cayep-course-cancel {
//                     border: 1px solid rgba(240, 237, 234, 0.12);
//                     background: rgba(255, 255, 255, 0.035);
//                     color: #AAB2B6;
//                 }

//                 .cayep-course-save {
//                     border: 1px solid #F6C944;
//                     background: #F6C944;
//                     color: #0A0F12;
//                 }

//                 .cayep-course-delete:hover {
//                     background: rgba(180, 35, 24, 0.15);
//                 }

//                 .cayep-course-cancel:hover {
//                     color: #F0EDEA;
//                 }

//                 .cayep-course-save:hover {
//                     filter: brightness(1.06);
//                 }

//                 .cayep-course-delete:disabled,
//                 .cayep-course-cancel:disabled,
//                 .cayep-course-save:disabled,
//                 .cayep-course-edit-close:disabled {
//                     opacity: 0.45;
//                     cursor: not-allowed;
//                 }

//                 .cayep-edit-swal-popup {
//                     width: min(430px, calc(100vw - 28px)) !important;
//                     border-radius: 17px !important;
//                     padding: 24px !important;
//                     font-family: inherit !important;
//                 }

//                 .cayep-edit-swal-title {
//                     color: #062A4B !important;
//                     font-size: 20px !important;
//                     font-weight: 800 !important;
//                 }

//                 .cayep-edit-swal-text {
//                     color: #526273 !important;
//                     font-size: 13px !important;
//                     line-height: 1.65 !important;
//                 }

//                 .cayep-edit-swal-confirm,
//                 .cayep-edit-swal-cancel {
//                     min-height: 39px !important;
//                     border-radius: 8px !important;
//                     font-size: 12px !important;
//                     font-weight: 700 !important;
//                 }

//                 .swal2-timer-progress-bar {
//                     background: #F6C944 !important;
//                 }

//                 @media (max-width: 720px) {
//                     .cayep-course-edit-overlay {
//                         align-items: flex-start;
//                         padding: 10px;
//                     }

//                     .cayep-course-edit-modal {
//                         max-height: calc(100vh - 20px);
//                     }

//                     .cayep-course-edit-header {
//                         padding: 20px;
//                     }

//                     .cayep-course-edit-body {
//                         padding: 15px;
//                         max-height: calc(100vh - 190px);
//                     }

//                     .cayep-course-edit-grid {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-course-edit-field.full {
//                         grid-column: auto;
//                     }

//                     .cayep-course-media-box,
//                     .cayep-course-video-box {
//                         align-items: flex-start;
//                         flex-direction: column;
//                     }

//                     .cayep-course-current-media {
//                         width: 100%;
//                         height: 180px;
//                     }

//                     .cayep-course-media-actions {
//                         justify-content: flex-start;
//                     }

//                     .cayep-resource-details-grid {
//                         grid-template-columns: 1fr;
//                     }

//                     .cayep-resource-upload-top {
//                         align-items: flex-start;
//                         flex-direction: column;
//                     }

//                     .cayep-pending-resource {
//                         align-items: flex-start;
//                     }

//                     .cayep-resource-remove-pending {
//                         flex-shrink: 0;
//                     }

//                     .cayep-course-edit-footer {
//                         align-items: stretch;
//                         flex-direction: column;
//                         padding: 15px;
//                     }

//                     .cayep-course-edit-footer-right {
//                         width: 100%;
//                     }

//                     .cayep-course-delete,
//                     .cayep-course-cancel,
//                     .cayep-course-save {
//                         flex: 1;
//                     }
//                 }

//                 @media (max-width: 480px) {
//                     .cayep-course-edit-header h2 {
//                         font-size: 20px;
//                     }

//                     .cayep-course-edit-header p {
//                         max-width: 240px;
//                         line-height: 1.5;
//                     }

//                     .cayep-course-edit-section {
//                         padding: 15px;
//                     }

//                     .cayep-existing-resource {
//                         align-items: flex-start;
//                     }

//                     .cayep-existing-resource-actions {
//                         flex-direction: column;
//                     }

//                     .cayep-course-edit-footer-right {
//                         flex-direction: column;
//                     }

//                     .cayep-course-cancel,
//                     .cayep-course-save {
//                         width: 100%;
//                     }
//                 }
//             `}</style>
//         </div>
//     );
// };

// export default ViewCoursesEdit;